import M from "materialize-css";

import { auth } from "../../typings/auth";
import { STATE } from "../../thor.const";
import { NavLink, NavLinkController } from "../nav-link/nav-link.controller";

export interface SidenavLink extends NavLink {
    acl?: string;
    icon?: string;
    secondary?: boolean;
    divider?: boolean;
    external?: boolean;
    target?: string;
}

interface LinkElem extends SidenavLink {
    subLinks: SidenavLink[];
}

interface LinkGroup {
    collapsibleGroup?: boolean;
    expandedGroup?: boolean;
    elems: LinkElem[];
}

export class SidenavController extends NavLinkController {
    public static $inject: string[] = [
        "$mdSidenav",
        "$translate",
        "authService",
        "contractService",
        "configService",
        "sitesService",
        "toaster"
    ];

    /**
     * These backgrounds should have hi-res quality with a resolution of 600*300 minimum
     */
    private static backgrounds: string[] = [
        "https://static.interencheres.com/Atlas/menu/IMG_3830.jpg",
        "https://static.interencheres.com/Atlas/menu/IMG_2666.jpg",
        "https://static.interencheres.com/Atlas/menu/IMG_2667.jpg",
        "https://static.interencheres.com/Atlas/menu/IMG_2202.jpg"
    ];

    public background: string;
    public links: (LinkGroup | SidenavLink)[];

    private $t;

    constructor(
        private $mdSidenav: ng.material.ISidenavService,
        private $translate: ng.translate.ITranslateService,
        protected authService,
        private contractService,
        private configService,
        private sitesService,
        toaster
    ) {
        super(authService, toaster);
        this.$t = this.$translate.instant;
    }

    public $onInit() {
        const organization = this.authService.organization || {};
        const canPublishToInterencheres = this.sitesService.organizationCanPublishToInterencheres(organization);
        const hasOrganizationRole = this.authService.role && this.authService.role !== auth.Roles.Visitor;
        const { isStripeContractActivated } = this.contractService;
        const isInternational = this.authService.isInternational();

        this.links = [
            {
                expandedGroup: true,
                elems: [
                    {
                        icon: "event",
                        title: this.$t("ventes_live_online_et_salle"),
                        acl: auth.AclAccess.Sales,
                        subLinks: [
                            {
                                path: STATE.V2.SALES.FUTURE.url,
                                external: true,
                                target: "_self",
                                title: this.$t("ventes_a_venir"),
                                acl: auth.AclAccess.Sales
                            },
                            {
                                path: STATE.V2.SALES.PAST.url,
                                external: true,
                                target: "_self",
                                title: this.$t("ventes_passees"),
                                acl: auth.AclAccess.Sales
                            },
                            {
                                path: STATE.V2.SALES.PROMPTER.url,
                                external: true,
                                target: "_self",
                                title: this.$t("prompter"),
                                acl: auth.AclAccess.Sales
                            }
                        ]
                    }
                ]
            },
            {
                ...this.getRestriction(auth.Scopes.BA_ACCESS),
                path: STATE.V2.SEARCH.url,
                external: true,
                target: "_self",
                icon: "search",
                title: this.$t("base_d_adjudications"),
                acl: auth.AclAccess.Search,
                divider: true
            },
            ...(canPublishToInterencheres ? [{
                path: STATE.V2.MEETUPS.url,
                external: true,
                target: "_self",
                title: this.$t("journees_d_expertises"),
                acl: auth.AclAccess.Organization
            }] : []),
            {
                collapsibleGroup: true,
                elems: [
                    ...(hasOrganizationRole ? [{
                        icon: "equalizer",
                        title: this.$t("statistiques"),
                        acl: auth.AclAccess.Analytics,
                        subLinks: [
                            {
                                ...this.getRestriction(auth.Scopes.STATS_ACTIVITY),
                                path: STATE.V2.STATS_ACTIVITY.url,
                                external: true,
                                target: "_self",
                                title: this.$t("stats_activity"),
                                acl: auth.AclAccess.Organization
                            },
                            {
                                path: STATE.V2.STATS.FUTURE.url,
                                external: true,
                                target: "_self",
                                title: this.$t("ventes_a_venir"),
                                icon: "calendar_blank",
                                acl: auth.AclAccess.Analytics
                            },
                            {
                                path: STATE.V2.STATS.PAST.url,
                                external: true,
                                target: "_self",
                                title: this.$t("ventes_passees"),
                                icon: "calendar_check",
                                acl: auth.AclAccess.Analytics
                            }
                        ]
                    }] : []),
                    {
                        icon: "person",
                        title: this.$t("mon_compte"),
                        acl: auth.AclAccess.Organization,
                        subLinks: [
                            {
                                ...this.getRestriction(auth.Scopes.ORGANIZATION_SETTINGS),
                                path: STATE.V2.ORGANIZATION.MAIN.url,
                                external: true,
                                target: "_self",
                                icon: "domain",
                                title: this.$t("organisation"),
                                acl: auth.AclAccess.Organization
                            },
                            {
                                ...this.getRestriction(auth.Scopes.ORGANIZATION_SETTINGS),
                                path: STATE.V2.ORGANIZATION.MY_PAGE.url,
                                external: true,
                                target: "_self",
                                icon: "book_open_page",
                                title: this.$t("presentation_structures"),
                                acl: auth.AclAccess.Organization
                            },
                            {
                                ...this.getRestriction(auth.Scopes.ORGANIZATION_SETTINGS),
                                path: STATE.V2.ORGANIZATION.SALES_CONDITIONS.url,
                                external: true,
                                target: "_self",
                                title: this.$t("frais_et_conditions"),
                                acl: auth.AclAccess.Organization
                            },
                            {
                                ...this.getRestriction(auth.Scopes.ORGANIZATION_SETTINGS),
                                path: STATE.V2.ORGANIZATION.SALE.url,
                                external: true,
                                target: "_self",
                                title: this.$t("parametres_sale"),
                                acl: auth.AclAccess.Organization
                            },
                            ...(hasOrganizationRole
                                ? [
                                      {
                                        ...this.getRestriction(auth.Scopes.ORGANIZATION_SETTINGS),
                                          path: STATE.V2.ORGANIZATION.PAYMENT_CONTRACTS.url,
                                          external: true,
                                          target: "_self",
                                          title: this.$t("paiement_stripe"),
                                          acl: auth.AclAccess.Organization
                                      }
                                  ]
                                : []),
                            ...(isStripeContractActivated
                                ? [
                                      {
                                          ...this.getRestriction(auth.Scopes.PAYOUTS),
                                          path: STATE.V2.PAYOUTS.MAIN.url,
                                          external: true,
                                          target: "_self",
                                          title: this.$t("suivi_des_paiements_stripe"),
                                          acl: auth.AclAccess.Organization
                                      }
                                  ]
                                : []),
                            {
                                path: STATE.V2.ORGANIZATION.AUTHORIZATION.url,
                                external: true,
                                target: "_self",
                                icon: "lock_open",
                                title: this.$t("demandes_d_acces"),
                                acl: auth.AclAccess.OrganizationAuthorization
                            },
                            {
                                path: STATE.V2.ORGANIZATION.PREFERENCES.url,
                                external: true,
                                target: "_self",
                                title: this.$t("preferences_techniques")
                            }
                        ]
                    }
                ]
            },
            ...(canPublishToInterencheres ? [{
                path: this.configService.supportIEUrl,
                title: this.$t("centre_d_aide_et_documentation"),
                external: true,
                acl: auth.AclAccess.Documentation
            }] : []),
            {
                collapsibleGroup: true,
                elems: [
                    {
                        title: "TEMIS",
                        acl: auth.AclAccess.Temis,
                        subLinks: [
                            {
                                title: `${this.$t("presentation_du_service")} FR/EN`,
                                path: "https://static.interencheres.com/doc-adherents/TEMIS+VF-VUK.pdf",
                                external: true
                            },
                            ...(canPublishToInterencheres
                                ? [
                                      {
                                          title: `${this.$t("formulaire_d_abonnement")} FR`,
                                          path: "https://form.jotform.com/210181925745355",
                                          external: true
                                      },
                                      {
                                          title: `${this.$t("formulaire_d_abonnement")} EN`,
                                          path: "https://form.jotform.com/220864325671356",
                                          external: true
                                      }
                                  ]
                                : [])
                        ]
                    }
                ]
            },
            ...(canPublishToInterencheres && !isInternational
                ? [
                      {
                          title: this.$t("commandez_vos_cartons"),
                          path: "https://www.raja.fr/INTERSHOP/web/WFS/RAJA-FR-Site/fr_FR/-/EUR/AffiliatePrefix-Start?PartnerUUID=htoK_cmqexQAAAFyxcyjG5na",
                          external: true
                      }
                  ]
                : []),
            {
                expandedGroup: true,
                divider: true,
                elems: [
                    {
                        title: this.$t("support"),
                        secondary: true,
                        subLinks: [
                            {
                                path: this.sitesService.contactSupport.tel_link,
                                title: this.$t("support_tel",
                                { NUMBER: this.authService.isInternational() ?
                                    this.sitesService.contactSupport.tel_inter : this.sitesService.contactSupport.tel
                                }),
                                secondary: true,
                                external: true
                            },
                            {
                                path: this.sitesService.contactSupport.email_link,
                                title: this.$t("support_email", { EMAIL: this.sitesService.contactSupport.email }),
                                secondary: true,
                                external: true
                            }
                        ]
                    }
                ]
            },
            {
                path: STATE.V2.LOKI.ORGANIZATIONS.url,
                title: "Loki",
                icon: "supervisor_account",
                acl: auth.AclAccess.Loki
            }
        ];

        this.initMCollapsible();
        this.background = this._getRandomBackground();
    }

    public toggleLeft() {
        this.$mdSidenav("menu").toggle();
    }

    public close() {
        angular.element(document).ready(() => {
            const elems = document.querySelectorAll(".sidenav-menu");
            const sidenav = M.Sidenav.getInstance(elems[0]);
            sidenav.close();
        });
    }

    private _getRandomBackground() {
        return SidenavController.backgrounds[
            Math.floor(Math.random() * SidenavController.backgrounds.length)
        ];
    }

    private initMCollapsible() {
        angular.element(document).ready(() => {
            const elems = document.querySelectorAll(".collapsible");
            M.Collapsible.init(elems, {});
        });
    }
}
